import React, { useState, useRef, useEffect } from "react";
import { AuthModal, useAuth } from "./Auth";

const API = "https://api.assetgraph.ai";

const STARTERS = [
  { icon: "🔍", text: "Scan GitHub repo", prompt: "Scan https://github.com/Uniswap/v2-core and find vulnerabilities" },
  { icon: "🌐", text: "Check a domain", prompt: "Scan summer.fi and find attack paths" },
  { icon: "⚡", text: "Try DefiTuna", prompt: "Scan defituna.com and find attack paths" },
];

const GITHUB_STARTERS = [
  { icon: "🔍", text: "Scan a repo", prompt: "Scan https://github.com/" },
  { icon: "🛡️", text: "Scan for secrets", prompt: "Scan https://github.com/ and find leaked secrets and private keys" },
  { icon: "⚡", text: "Full attack path", prompt: "Scan https://github.com/ and find complete attack paths to critical assets" },
];

const STEP_LABELS = {
  scope: "Loading scope", supply_chain: "Scanning dependencies",
  osv: "Checking CVEs", secrets: "Scanning secrets",
  infra: "Infra recon", attack_paths: "Computing attack paths",
};

function VerdictBadge({ verdict }) {
  const colors = { PROVEN:"#FF4D6A",CRITICAL:"#FF4D6A",LIKELY:"#FFB547",HIGH:"#FFB547",POSSIBLE:"#4D9FEC",MEDIUM:"#4D9FEC",REFUTED:"#00E87A",LOW:"#8B95A8" };
  const c = colors[verdict] || "#8B95A8";
  return <span style={{ background:c+"20", color:c, border:"1px solid "+c+"60", borderRadius:4, padding:"2px 8px", fontSize:11, fontFamily:"monospace", fontWeight:700 }}>{verdict}</span>;
}

function FindingCard({ finding }) {
  const [open, setOpen] = useState(false);
  const verdict = finding.verdict || "POSSIBLE";
  const borders = { PROVEN:"#FF4D6A",CRITICAL:"#FF4D6A",LIKELY:"#FFB547",HIGH:"#FFB547",POSSIBLE:"#4D9FEC",REFUTED:"#00E87A" };
  const border = borders[verdict] || "#2A3347";
  return (
    <div onClick={() => setOpen(!open)} style={{ border:"1px solid "+border, borderRadius:8, marginBottom:8, cursor:"pointer", background:"#0E1118", overflow:"hidden" }}>
      <div style={{ display:"flex", alignItems:"center", gap:8, padding:"12px 16px" }}>
        <VerdictBadge verdict={verdict} />
        <span style={{ color:"#E8EBF0", fontSize:13, flex:1 }}>{finding.title || "Finding"}</span>
        {finding.blast_radius != null && <span style={{ color:"#525E72", fontSize:11, fontFamily:"monospace" }}>{Number(finding.blast_radius).toFixed(1)}/10</span>}
        <span style={{ color:"#525E72", fontSize:11 }}>{open ? "▲" : "▼"}</span>
      </div>
      {open && (
        <div style={{ borderTop:"1px solid #1E2535", padding:16 }}>
          {finding.description && <p style={{ color:"#8B95A8", fontSize:13, lineHeight:1.7, marginBottom:12 }}>{finding.description}</p>}
          {finding.asset && <div style={{ marginBottom:12 }}><div style={{ color:"#525E72", fontSize:10, fontFamily:"monospace", marginBottom:4 }}>ASSET</div><code style={{ color:"#4D9FEC", fontSize:12, background:"#141820", padding:"4px 10px", borderRadius:4 }}>{finding.asset}</code></div>}
          {finding.recommendation && (
            <div style={{ marginBottom:12 }}>
              <div style={{ color:"#525E72", fontSize:10, fontFamily:"monospace", marginBottom:4 }}>REMEDIATION</div>
              <div style={{ background:"#141820", borderLeft:"3px solid #00E87A", borderRadius:"0 6px 6px 0", padding:"10px 14px" }}>
                <p style={{ color:"#8B95A8", fontSize:13, lineHeight:1.7, margin:0 }}>{finding.recommendation}</p>
              </div>
            </div>
          )}
          {finding.rag_context && (
            <div>
              <div style={{ color:"#525E72", fontSize:10, fontFamily:"monospace", marginBottom:4 }}>KNOWLEDGE BASE</div>
              <div style={{ background:"#141820", borderLeft:"2px solid #2A3347", borderRadius:"0 6px 6px 0", padding:"10px 14px" }}>
                <p style={{ color:"#525E72", fontSize:11, lineHeight:1.6, margin:0 }}>{finding.rag_context.slice(0,400)}</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ExportBar({ findings, scanId, tier1Findings, scanComplete }) {
  const doExport = async (fmt) => {
    if (!scanComplete) { alert("Please wait for the scan to fully complete before exporting."); return; }
    try {
      const res = await fetch(API+"/export/"+fmt, { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({findings, tier1_findings: tier1Findings||[], scan_id:scanId||"scan"}) });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href=url; a.download="assetgraph."+fmt; a.click(); URL.revokeObjectURL(url);
    } catch(e) { alert("Export error: "+e.message); }
  };
  return (
    <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:10 }}>
      <span style={{ color:"#525E72", fontSize:11, fontFamily:"monospace" }}>EXPORT:</span>
      <button onClick={() => doExport("sarif")} style={{ background:"transparent", border:"1px solid #4D9FEC", borderRadius:6, padding:"3px 12px", color:"#4D9FEC", cursor:"pointer", fontSize:11, fontFamily:"monospace" }}>SARIF</button>
      <button onClick={() => doExport("json")} style={{ background:"transparent", border:"1px solid #4D9FEC", borderRadius:6, padding:"3px 12px", color:"#4D9FEC", cursor:"pointer", fontSize:11, fontFamily:"monospace" }}>JSON</button>
    </div>
  );
}

function ProgressSteps({ steps }) {
  const icons = { running:"⟳", done:"✓", error:"✗" };
  const colors = { running:"#FFB547", done:"#00E87A", error:"#FF4D6A" };
  return (
    <div style={{ background:"#0E1118", border:"1px solid #1E2535", borderRadius:8, padding:"12px 16px", marginTop:8 }}>
      {steps.map((s,i) => (
        <div key={i} style={{ display:"flex", alignItems:"center", gap:8, padding:"3px 0", fontFamily:"monospace", fontSize:12 }}>
          <span style={{ color:colors[s.status]||"#8B95A8", width:14 }}>{icons[s.status]||"○"}</span>
          <span style={{ color:"#8B95A8" }}>{STEP_LABELS[s.step]||s.step}</span>
          {s.detail && <span style={{ color:"#525E72" }}>— {s.detail}</span>}
        </div>
      ))}
    </div>
  );
}


function Tier1FindingCard({ finding }) {
  const [open, setOpen] = React.useState(false);
  if (!finding) return null;
  const sev = finding.severity || "note";
  const sevColor = {error:"#FF4D6A",warning:"#FFB547",note:"#4D9FEC",none:"#525E72"}[sev]||"#525E72";
  const statusLabel = finding.on_chain_status?.tx_count===0?"NEVER USED":finding.on_chain_status?.active?"ACTIVE":"DORMANT";
  const statusColor = finding.on_chain_status?.active?"#FF4D6A":finding.on_chain_status?.tx_count===0?"#00E87A":"#FFB547";
  const envColor = finding.environment==="TESTNET"?"#FFB547":finding.environment==="MAINNET"?"#FF4D6A":"#525E72";
  const conf = finding.confidence||{};
  const confBar = (val) => (
    <div style={{display:"flex",alignItems:"center",gap:6}}>
      <div style={{flex:1,background:"#1E2535",borderRadius:4,height:6}}>
        <div style={{width:(val||0)+"%",background:val>=90?"#00E87A":val>=70?"#FFB547":"#FF4D6A",borderRadius:4,height:6}}/>
      </div>
      <span style={{color:"#E8EBF0",fontSize:11,fontFamily:"monospace",minWidth:32}}>{val||0}%</span>
    </div>
  );
  return (
    <div style={{border:"1px solid "+sevColor,borderRadius:10,marginBottom:10,background:"#0E1118",overflow:"hidden"}}>
      <div onClick={()=>setOpen(!open)} style={{padding:"12px 16px",cursor:"pointer",display:"flex",alignItems:"center",gap:8}}>
        <span style={{background:sevColor+"20",color:sevColor,border:"1px solid "+sevColor+"60",borderRadius:4,padding:"2px 8px",fontSize:11,fontFamily:"monospace",fontWeight:700}}>{sev.toUpperCase()}</span>
        <span style={{color:"#E8EBF0",fontSize:13,flex:1}}>{finding.secret_type?.replace(/_/g," ")}</span>
        <span style={{color:envColor,fontSize:10,fontFamily:"monospace",background:envColor+"20",padding:"2px 6px",borderRadius:4}}>{finding.environment} · {finding.network}</span>
        <span style={{color:statusColor,fontSize:11,fontFamily:"monospace",fontWeight:700}}>{statusLabel}</span>
        <span style={{color:"#525E72",fontSize:11}}>{open?"▲":"▼"}</span>
      </div>
      {open && (
        <div style={{borderTop:"1px solid #1E2535",padding:16}}>
          <div style={{marginBottom:16,background:"#141820",borderRadius:8,padding:"12px 16px"}}>
            <div style={{color:"#525E72",fontSize:10,fontFamily:"monospace",letterSpacing:"0.1em",marginBottom:10}}>ATTACK CHAIN</div>
            {[
              ["SECRET",finding.secret_type?.replace(/_/g," ")||"PRIVATE KEY","#FF4D6A"],
              ["DERIVES TO",finding.identity?.address||"unknown","#4D9FEC"],
              ["CHAIN",(finding.network||"Ethereum")+" / "+(finding.environment||"UNKNOWN"),"#FFB547"],
              ["STATUS",statusLabel,statusColor],
              ["AUTHORITY",finding.authority?.type||"UNKNOWN",finding.authority?.type&&finding.authority.type!=="UNKNOWN"?"#FF4D6A":"#525E72"],
              ["CONTRACT",finding.authority?.target||"Not determined","#4D9FEC"],
              ["CAPABILITY",finding.authority?.capabilities?.join(", ")||"Not determined","#FFB547"],
            ].map(([label,value,color],i)=>(
              <div key={label} style={{display:"flex",alignItems:"flex-start",marginBottom:6}}>
                <div style={{width:100,color:"#525E72",fontSize:10,fontFamily:"monospace",paddingTop:2,flexShrink:0}}>{label}</div>
                <div style={{display:"flex",flexDirection:"column",alignItems:"flex-start"}}>
                  {i>0&&<div style={{color:"#2A3347",fontSize:12,marginBottom:2}}>↓</div>}
                  <code style={{color:color,fontSize:12,background:color+"10",padding:"2px 8px",borderRadius:4}}>{value}</code>
                </div>
              </div>
            ))}
          </div>
          {finding.rpc_evidence&&finding.rpc_evidence.length>0&&(
            <div style={{marginBottom:16}}>
              <div style={{color:"#525E72",fontSize:10,fontFamily:"monospace",letterSpacing:"0.1em",marginBottom:6}}>ON-CHAIN EVIDENCE</div>
              {finding.rpc_evidence.map((ev,i)=>(
                <div key={i} style={{background:"#141820",borderLeft:"3px solid #00E87A",borderRadius:"0 6px 6px 0",padding:"8px 12px",marginBottom:4,fontFamily:"monospace",fontSize:11,color:"#00E87A"}}>{ev}</div>
              ))}
            </div>
          )}
          <div style={{marginBottom:16}}>
            <div style={{color:"#525E72",fontSize:10,fontFamily:"monospace",letterSpacing:"0.1em",marginBottom:6}}>ACTIVITY</div>
            <div style={{background:"#141820",borderRadius:6,padding:"10px 14px",display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,fontSize:12}}>
              <div><span style={{color:"#525E72"}}>Transactions: </span><span style={{color:"#E8EBF0"}}>{finding.on_chain_status?.tx_count??"Unknown"}</span></div>
              <div><span style={{color:"#525E72"}}>Status: </span><span style={{color:statusColor,fontWeight:700}}>{statusLabel}</span></div>
              {finding.on_chain_status?.balance_wei>0&&<div><span style={{color:"#525E72"}}>Balance: </span><span style={{color:"#FFB547"}}>{(finding.on_chain_status.balance_wei/1e18).toFixed(4)} ETH</span></div>}
              {finding.on_chain_status?.last_tx_timestamp&&<div><span style={{color:"#525E72"}}>Last tx: </span><span style={{color:"#E8EBF0"}}>{Math.floor((Date.now()/1000-finding.on_chain_status.last_tx_timestamp)/86400)}d ago</span></div>}
            </div>
          </div>
          {conf.overall!==undefined&&(
            <div style={{marginBottom:16}}>
              <div style={{color:"#525E72",fontSize:10,fontFamily:"monospace",letterSpacing:"0.1em",marginBottom:8}}>CONFIDENCE</div>
              <div style={{background:"#141820",borderRadius:6,padding:"10px 14px"}}>
                {[["Secret",conf.secret],["Identity",conf.identity],["Authority",conf.authority],["Activity",conf.activity]].map(([k,v])=>(
                  <div key={k} style={{display:"grid",gridTemplateColumns:"80px 1fr",gap:8,alignItems:"center",marginBottom:6}}>
                    <span style={{color:"#525E72",fontSize:11,fontFamily:"monospace"}}>{k}</span>
                    {confBar(v)}
                  </div>
                ))}
                <div style={{borderTop:"1px solid #1E2535",paddingTop:8,marginTop:4}}>
                  <div style={{display:"grid",gridTemplateColumns:"80px 1fr",gap:8,alignItems:"center"}}>
                    <span style={{color:"#E8EBF0",fontSize:12,fontFamily:"monospace",fontWeight:700}}>OVERALL</span>
                    {confBar(conf.overall)}
                  </div>
                </div>
              </div>
            </div>
          )}
          <div style={{display:"flex",gap:8,alignItems:"stretch"}}>
            <div style={{flex:1,background:sevColor+"15",border:"1px solid "+sevColor+"40",borderRadius:8,padding:"10px 14px",textAlign:"center"}}>
              <div style={{color:"#525E72",fontSize:10,fontFamily:"monospace",marginBottom:4}}>VERDICT</div>
              <div style={{color:sevColor,fontSize:16,fontFamily:"monospace",fontWeight:700}}>{finding.verdict?.toUpperCase()}</div>
              <div style={{color:"#525E72",fontSize:10,marginTop:4}}>{finding.severity_reason}</div>
            </div>
            <div style={{flex:2,background:"#141820",borderRadius:8,padding:"10px 14px"}}>
              <div style={{color:"#525E72",fontSize:10,fontFamily:"monospace",marginBottom:4}}>SOURCE</div>
              <code style={{color:"#4D9FEC",fontSize:11}}>{finding.source?.file}:{finding.source?.line}</code>
              <div style={{color:"#525E72",fontSize:10,marginTop:4}}>{finding.verdict_reason}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Message({ msg, scanComplete }) {
  const isUser = msg.role === "user";
  return (
    <div style={{ display:"flex", justifyContent:isUser?"flex-end":"flex-start", marginBottom:20, padding:"0 20px" }}>
      {!isUser && <div style={{ width:32, height:32, borderRadius:"50%", background:"#00E87A15", border:"1px solid #004D29", display:"flex", alignItems:"center", justifyContent:"center", color:"#00E87A", fontSize:11, fontWeight:700, marginRight:12, flexShrink:0, marginTop:2, fontFamily:"monospace" }}>AG</div>}
      <div style={{ maxWidth:"78%" }}>
        {msg.content && <div style={{ background:isUser?"#141820":"transparent", border:isUser?"1px solid #1E2535":"none", borderRadius:12, padding:isUser?"10px 16px":"2px 0", color:"#E8EBF0", fontSize:14, lineHeight:1.7, whiteSpace:"pre-wrap" }}>{msg.content}</div>}
        {msg.progress && msg.progress.length > 0 && <ProgressSteps steps={msg.progress} />}
        {msg.tier1_findings && msg.tier1_findings.length > 0 && (
          <div style={{marginTop:12}}>
            <div style={{color:"#525E72",fontSize:11,fontFamily:"monospace",marginBottom:8,letterSpacing:"0.1em"}}>
              TIER 1 ANALYSIS — {msg.tier1_findings.length} KEY{msg.tier1_findings.length>1?"S":""} VALIDATED
            </div>
            {msg.tier1_findings.map((f,i)=><Tier1FindingCard key={i} finding={f}/>)}
          </div>
        )}
        {msg.findings && msg.findings.length > 0 && (
          <div style={{ marginTop:12 }}>
            <div style={{ color:"#525E72", fontSize:11, fontFamily:"monospace", marginBottom:8, letterSpacing:"0.1em" }}>{msg.findings.length} FINDINGS — CLICK TO EXPAND</div>
            <ExportBar findings={msg.findings} scanId={msg.scanId} tier1Findings={msg.tier1_findings} scanComplete={scanComplete} />
            {msg.findings.map((f,i) => <FindingCard key={i} finding={f} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function PaywallModal({ onClose }) {
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.85)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ background:"#0E1118", border:"1px solid #1E2535", borderRadius:16, padding:32, maxWidth:460, width:"90%" }}>
        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:20 }}>
          <div>
            <div style={{ color:"#00E87A", fontSize:10, fontFamily:"monospace", letterSpacing:"0.15em", marginBottom:6 }}>PRO FEATURE</div>
            <h2 style={{ color:"#fff", fontSize:20, fontWeight:700, margin:0 }}>Upgrade to Pro — $20/mo</h2>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", color:"#525E72", cursor:"pointer", fontSize:20 }}>x</button>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:24 }}>
          {[["Secrets scanning","EVM key derivation"],["Attack paths","Cross-layer chains"],["RAG context","1,232 incidents"],["SARIF export","CI/CD integration"]].map(([t,s]) => (
            <div key={t} style={{ background:"#141820", borderRadius:8, padding:"10px 14px" }}>
              <div style={{ color:"#E8EBF0", fontSize:13, fontWeight:600, marginBottom:2 }}>{t}</div>
              <div style={{ color:"#525E72", fontSize:11 }}>{s}</div>
            </div>
          ))}
        </div>
        <button onClick={async () => { try { const r=await fetch(API+"/billing/checkout",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({plan:"pro",cycle:"monthly"})}); const d=await r.json(); if(d.url) window.location.href=d.url; else alert(d.message||"Add Stripe keys to .env"); } catch(e){alert("Configure Stripe in backend/.env");} onClose(); }} style={{ width:"100%", background:"#00E87A", border:"none", borderRadius:10, padding:"12px", color:"#000", fontWeight:700, fontSize:14, cursor:"pointer" }}>
          Upgrade - $20/month
        </button>
        <div style={{ marginTop:12, textAlign:"center", color:"#525E72", fontSize:11 }}>14-day free trial · Cancel anytime</div>
      </div>
    </div>
  );
}

export default function App() {
  const [messages, setMessages] = useState([{ role:"assistant", content:"Ready. Paste a GitHub URL or domain to scan for attack paths." }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [scanComplete, setScanComplete] = useState(false);
  const { user, token, logout, setUser, setToken } = useAuth();
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior:"smooth" }); }, [messages]);

  const [githubConnected, setGithubConnected] = useState(false);
  const [gitlabConnected, setGitlabConnected] = useState(false);
  const [githubScans, setGithubScans] = useState([]);

  useEffect(() => {
    fetch("https://api.assetgraph.ai/github/scans")
      .then(r => r.json())
      .then(data => { if(data.scans) setGithubScans(data.scans); })
      .catch(() => {});
  }, []);

  // Handle OAuth callbacks on page load
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const installationId = params.get("installation_id");
    const setupAction = params.get("setup_action");
    const code = params.get("code");
    const state = params.get("state");

    // GitHub App installation callback
    if (installationId && setupAction === "install") {
      fetch("https://api.assetgraph.ai/github/save-installation", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { "Authorization": "Bearer " + token } : {})
        },
        body: JSON.stringify({ installation_id: installationId })
      }).then(() => {
        setGithubConnected(true);
        localStorage.setItem("github_connected", "true");
        // Clean URL
        window.history.replaceState({}, "", "/");
      }).catch(() => setGithubConnected(true));
    }

    // Check stored connection state
    if (localStorage.getItem("github_connected") === "true") {
      setGithubConnected(true);
    }
    if (localStorage.getItem("gitlab_connected") === "true") {
      setGitlabConnected(true);
    }
  }, [token]);


  const sendMessage = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages(prev => [...prev, { role:"user", content:msg }]);
    setLoading(true);
    setScanComplete(false);
    const newMsg = { role:"assistant", content:"", progress:[], findings:[] };
    setMessages(prev => [...prev, newMsg]);
    try {
      const res = await fetch(API+"/chat/message", {
        method:"POST",
        headers:{ "Content-Type":"application/json", ...(token?{"Authorization":"Bearer "+token}:{}) },
        body:JSON.stringify({ message:msg, plan:user?user.plan:"free" }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while(true) {
        const {done,value} = await reader.read();
        if(done) break;
        buffer += decoder.decode(value,{stream:true});
        const lines = buffer.split("\n");
        buffer = lines.pop();
        for(const line of lines) {
          if(!line.startsWith("data: ")) continue;
          try {
            const event = JSON.parse(line.slice(6));
            setMessages(prev => {
              const msgs = [...prev];
              const last = {...msgs[msgs.length-1]};
              if(event.type==="token") last.content=(last.content||"")+event.content;
              else if(event.type==="progress") {
                const steps=[...(last.progress||[])];
                const idx=steps.findIndex(s=>s.step===event.step);
                if(idx>=0) steps[idx]=event; else steps.push(event);
                last.progress=steps;
              } else if(event.type==="findings") { last.findings=event.data||[]; }
              else if(event.type==="tier1_findings") { last.tier1_findings=event.data||[]; }
              else if(event.type==="paywall") { setShowPaywall(true); }
              msgs[msgs.length-1]=last;
              return msgs;
            });
          } catch(e) {}
        }
      }
    } catch(e) {
      setMessages(prev => { const msgs=[...prev]; msgs[msgs.length-1].content="Error: "+e.message; return msgs; });
    }
    setLoading(false);
    setScanComplete(true);
  };

  return (
    <>
      {showAuth && <AuthModal onClose={() => setShowAuth(false)} onSuccess={(u,t) => { setUser(u); setToken(t); setShowAuth(false); }} />}
      {showPaywall && <PaywallModal onClose={() => setShowPaywall(false)} />}
      <div style={{ display:"flex", height:"100vh", background:"#090B0F", color:"#E8EBF0", fontFamily:"Inter,system-ui,sans-serif" }}>
        <div style={{ width:240, background:"#0E1118", borderRight:"1px solid #1E2535", display:"flex", flexDirection:"column", padding:"16px 12px" }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:24, padding:"0 4px" }}>
            <div style={{ width:30, height:30, background:"#00E87A", borderRadius:7, display:"flex", alignItems:"center", justifyContent:"center", color:"#000", fontFamily:"monospace", fontSize:13, fontWeight:700 }}>AG</div>
            <span style={{ fontWeight:600, fontSize:15, letterSpacing:"-0.02em" }}>AssetGraph</span>
          </div>

          {user ? (
            <div style={{ marginBottom:16 }}>
              <div style={{ color:"#8B95A8", fontSize:11, marginBottom:6, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{user.email}</div>
              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
                <span style={{ fontSize:11, color:"#525E72", fontFamily:"monospace" }}>PLAN</span>
                <span style={{ fontSize:11, color:"#00E87A", fontFamily:"monospace", textTransform:"uppercase" }}>{user.plan||"free"}</span>
              </div>
              <button onClick={() => setShowPaywall(true)} style={{ width:"100%", background:"#00E87A15", border:"1px solid #004D29", borderRadius:8, padding:"7px", color:"#00E87A", cursor:"pointer", fontSize:12, fontWeight:600, marginBottom:8 }}>Upgrade - $20/mo</button>
              <button onClick={logout} style={{ width:"100%", background:"transparent", border:"1px solid #1E2535", borderRadius:8, padding:"6px", color:"#525E72", cursor:"pointer", fontSize:11 }}>Sign out</button>
            </div>
          ) : (
            <div style={{ marginBottom:16 }}>
              <button onClick={() => setShowAuth(true)} style={{ width:"100%", background:"#00E87A", border:"none", borderRadius:8, padding:"9px", color:"#000", cursor:"pointer", fontSize:13, fontWeight:700, marginBottom:8 }}>Sign in / Sign up</button>
              <div style={{ background:"#141820", borderRadius:6, padding:"8px 10px" }}>
                <div style={{ color:"#00E87A", fontSize:10, fontFamily:"monospace", marginBottom:3 }}>FREE TIER</div>
                <div style={{ color:"#525E72", fontSize:11 }}>3 free scans included</div>
              </div>
            </div>
          )}

          <button onClick={() => { setMessages([{role:"assistant",content:"Ready. Paste a GitHub URL or domain to scan."}]); setInput(""); }} style={{ background:"#141820", border:"1px solid #2A3347", borderRadius:8, padding:"9px 12px", color:"#E8EBF0", cursor:"pointer", fontSize:13, textAlign:"left", marginBottom:16 }}>+ New Scan</button>

          <div style={{ flex:1 }} />

  
        {/* GitHub Integration */}
        <div style={{ marginBottom: 16, padding: "10px 12px",
          background: "#141820", borderRadius: 8,
          border: "1px solid #1E2535" }}>
          <div style={{ color: "#525E72", fontSize: 10,
            fontFamily: "monospace", letterSpacing: "0.1em",
            marginBottom: 8 }}>INTEGRATIONS</div>
          {/* GitHub Button */}
          <button
            onClick={() => {
              if (githubConnected) {
                localStorage.removeItem("github_connected");
                setGithubConnected(false);
              } else {
                window.open("https://github.com/apps/assetgraph-security/installations/new", "_blank");
              }
            }}
            style={{ width: "100%",
              background: githubConnected ? "#00E87A15" : "#24292e",
              border: githubConnected ? "1px solid #00E87A" : "1px solid #444",
              borderRadius: 8, padding: "8px 10px",
              color: githubConnected ? "#00E87A" : "#fff",
              cursor: "pointer", fontSize: 12, fontWeight: 600,
              display: "flex", alignItems: "center",
              justifyContent: "center", gap: 8, marginBottom: 6 }}>
            <svg width="14" height="14" viewBox="0 0 24 24"
              fill={githubConnected ? "#00E87A" : "white"}>
              <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12z"/>
            </svg>
            {githubConnected ? "✓ GitHub Connected" : "Connect GitHub"}
          </button>

          {/* GitLab Button */}
          <button
            onClick={() => {
              if (gitlabConnected) {
                localStorage.removeItem("gitlab_connected");
                setGitlabConnected(false);
              } else {
                window.open("https://gitlab.com/oauth/authorize?client_id=assetgraph&redirect_uri=https://assetgraph.ai&response_type=code&scope=read_repository&state=gitlab", "_blank");
              }
            }}
            style={{ width: "100%",
              background: gitlabConnected ? "#00E87A15" : "#FC6D26",
              border: gitlabConnected ? "1px solid #00E87A" : "none",
              borderRadius: 8, padding: "8px 10px",
              color: gitlabConnected ? "#00E87A" : "#fff",
              cursor: "pointer", fontSize: 12, fontWeight: 600,
              display: "flex", alignItems: "center",
              justifyContent: "center", gap: 8 }}>
            <svg width="14" height="14" viewBox="0 0 24 24"
              fill={gitlabConnected ? "#00E87A" : "white"}>
              <path d="M22.65 14.39L12 22.13 1.35 14.39a.84.84 0 0 1-.3-.94l1.22-3.78 2.44-7.51A.42.42 0 0 1 4.82 2a.43.43 0 0 1 .58 0 .42.42 0 0 1 .11.18l2.44 7.49h8.1l2.44-7.51A.42.42 0 0 1 18.6 2a.43.43 0 0 1 .58 0 .42.42 0 0 1 .11.18l2.44 7.51L23 13.45a.84.84 0 0 1-.35.94z"/>
            </svg>
            {gitlabConnected ? "✓ GitLab Connected" : "Connect GitLab"}
          </button>
          <div style={{ color: "#525E72", fontSize: 10,
            marginTop: 6, textAlign: "center" }}>
            Auto-scan PRs for attack paths
          </div>
          {githubScans.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div style={{ color: "#525E72", fontSize: 10,
                fontFamily: "monospace", letterSpacing: "0.1em",
                marginBottom: 6 }}>RECENT PR SCANS</div>
              {githubScans.filter(s => s.repo !== "test/test").slice(0,5).map((scan, i) => (
                <div key={i} onClick={() => {
                  const findings = typeof scan.findings_json === "string"
                    ? JSON.parse(scan.findings_json || "[]")
                    : (scan.findings_json || []);
                  setMessages([{
                    role: "assistant",
                    content: "GitHub scan: " + scan.repo + " (" + scan.head_sha + ")" +
                      (scan.finding_count === 0 ? " — No findings" : ""),
                    findings: findings,
                    progress: []
                  }]);
                }} style={{ padding: "6px 8px", marginBottom: 4,
                  background: "#0E1118", borderRadius: 6,
                  cursor: "pointer", border: "1px solid #1E2535" }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ color: "#8B95A8", fontSize: 11,
                      overflow: "hidden", textOverflow: "ellipsis",
                      whiteSpace: "nowrap", maxWidth: 130 }}>
                      {scan.repo.split("/")[1] || scan.repo}
                    </span>
                    <span style={{ color: scan.critical_count > 0 ? "#FF4D6A" : "#00E87A",
                      fontSize: 10, fontFamily: "monospace", flexShrink: 0 }}>
                      {scan.critical_count > 0 ? scan.critical_count + " CRIT" : scan.finding_count + " found"}
                    </span>
                  </div>
                  <div style={{ color: "#525E72", fontSize: 10,
                    fontFamily: "monospace", marginTop: 2 }}>
                    {scan.head_sha.slice(0,7)} · {new Date(scan.created_at).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={{ padding:"12px 8px", borderTop:"1px solid #1E2535" }}>
            <div style={{ fontSize:10, color:"#525E72", fontFamily:"monospace", letterSpacing:"0.1em", marginBottom:6 }}>RAG KNOWLEDGE BASE</div>
            {[["defi_incidents","1,232"],["playbooks","21"],["compliance","16"]].map(([k,v]) => (
              <div key={k} style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:"#525E72", fontFamily:"monospace", padding:"2px 0" }}>
                <span>{k}</span><span style={{ color:"#00E87A" }}>{v}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ flex:1, display:"flex", flexDirection:"column", minWidth:0 }}>
          <div style={{ flex:1, overflowY:"auto", paddingTop:28, paddingBottom:8 }}>
            {messages.map((msg,i) => <Message key={i} msg={msg} scanComplete={scanComplete} />)}
            {loading && messages[messages.length-1]?.content==="" && (
              <div style={{ padding:"0 20px 0 64px" }}>
                <span style={{ color:"#525E72", fontFamily:"monospace", fontSize:12 }}>thinking...</span>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {messages.length <= 1 && (
            <div style={{ display:"flex", gap:8, padding:"0 20px 12px", flexWrap:"wrap", justifyContent:"center" }}>
              {STARTERS.map((s,i) => (
                <button key={i} onClick={() => sendMessage(s.prompt)} style={{ background:"#0E1118", border:"1px solid #1E2535", borderRadius:8, padding:"8px 16px", color:"#8B95A8", cursor:"pointer", fontSize:13 }}>{s.icon} {s.text}</button>
              ))}
            </div>
          )}

          <div style={{ padding:"12px 20px 20px", borderTop:"1px solid #1E2535" }}>
            <div style={{ display:"flex", gap:10, alignItems:"flex-end", background:"#0E1118", border:"1px solid #2A3347", borderRadius:14, padding:"10px 14px" }}>
              <textarea value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();sendMessage();} }} placeholder="Paste a GitHub URL or domain to scan..." disabled={loading} rows={1} style={{ flex:1, background:"transparent", border:"none", color:"#E8EBF0", fontSize:14, resize:"none", outline:"none", fontFamily:"inherit", lineHeight:1.6, maxHeight:120, overflowY:"auto" }} />
              <button onClick={() => sendMessage()} disabled={loading||!input.trim()} style={{ background:input.trim()&&!loading?"#00E87A":"#1E2535", border:"none", borderRadius:10, padding:"7px 18px", color:input.trim()&&!loading?"#000":"#525E72", cursor:input.trim()&&!loading?"pointer":"default", fontSize:13, fontWeight:600, flexShrink:0 }}>Send</button>
            </div>
            <div style={{ textAlign:"center", marginTop:8, fontSize:11, color:"#3A4356" }}>Enter to send · Shift+Enter for new line</div>
          </div>
        </div>
      </div>
    </>
  );
}
